import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CreditCard, Loader2, Users, TrendingUp, Timer, DollarSign, Activity, ArrowRight } from 'lucide-react';
import api from '@/lib/adminApi';
import { CountUp } from '@/components/Reveal';
import { EmptyState } from '@/components/admin/EmptyState';

const METRIC_META = {
  mrr: { label: 'MRR', icon: TrendingUp, prefix: '$', accent: '#17FDFD', sub: 'Monthly recurring revenue' },
  active_subscriptions: { label: 'Active Subscriptions', icon: CreditCard, accent: '#00FFA3', sub: 'Paying right now' },
  active_trials: { label: 'Active Trials', icon: Timer, accent: '#FFD700', sub: 'In trial period' },
  revenue: { label: 'Revenue (28d)', icon: DollarSign, prefix: '$', accent: '#FF2E93', sub: 'Last 28 days' },
  new_customers: { label: 'New Customers (28d)', icon: Users, accent: '#17FDFD', sub: 'Last 28 days' },
  active_users: { label: 'Active Users (28d)', icon: Activity, accent: '#00FFA3', sub: 'Last 28 days' },
};

const MetricCard = ({ id, value }) => {
  const m = METRIC_META[id];
  if (!m) return null;
  return (
    <div data-testid={`admin-rc-metric-${id}`} className="rounded-2xl border border-white/10 bg-surface/70 p-5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wider text-white/50">{m.label}</span>
        <span className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ background: `${m.accent}1a`, color: m.accent }}><m.icon className="h-4 w-4" /></span>
      </div>
      <div className="mt-3 font-display text-3xl font-bold text-white"><CountUp value={String(value ?? 0)} prefix={m.prefix || ''} /></div>
      <p className="mt-1 text-xs text-white/40">{m.sub}</p>
    </div>
  );
};

export default function AdminSubscriptions() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/revenuecat/metrics').then((r) => setMetrics(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>;

  if (!metrics?.connected) {
    return (
      <div className="space-y-6">
        <h1 className="font-display text-2xl font-bold text-white sm:text-3xl">Subscriptions</h1>
        <EmptyState icon={CreditCard} title="RevenueCat not connected" description="Add REVENUECAT_API_KEY to the backend to sync real subscription metrics and customers." />
      </div>
    );
  }

  const order = ['mrr', 'active_subscriptions', 'active_trials', 'revenue', 'new_customers', 'active_users'];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-2xl font-bold text-white sm:text-3xl">Subscriptions</h1>
        <p className="mt-1 text-sm text-white/50">Live data from RevenueCat — project Copy·Click ({metrics.currency}).</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {order.map((id) => <MetricCard key={id} id={id} value={metrics.metrics[id]} />)}
      </div>

      <Link to="/admin/users" data-testid="admin-subscriptions-to-users" className="flex items-center justify-between rounded-2xl border border-white/10 bg-surface/70 p-5 transition-colors hover:border-brand/30">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand/10 text-brand"><Users className="h-5 w-5" /></span>
          <div>
            <p className="font-display text-base font-semibold text-white">Browse all app users</p>
            <p className="text-xs text-white/50">Per-user subscriptions, entitlements, purchase history and management actions live in the Users section.</p>
          </div>
        </div>
        <ArrowRight className="h-5 w-5 text-white/40" />
      </Link>
    </div>
  );
}
